import { useState, useEffect } from 'react';
import { User, Building, Bell, Lock, Calendar, CalendarOff, SlidersHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from './useAuthSimple';
import unifiedApi from '../services/unifiedApi';
import logger from '@/utils/logger';

export const useSettings = () => {
  const { user } = useAuth();
  const isBasicPlan = (user?.school?.subscription_plan || 'basic').toLowerCase() !== 'premium';

  const [activeTab, setActiveTab] = useState('profile');
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: '',
  });

  const [securityData, setSecurityData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    busAlerts: true,
    routeUpdates: true,
    emergencyAlerts: true,
  });

  const [schoolData, setSchoolData] = useState({
    schoolName: '',
    address: '',
    phone: '',
    email: '',
    website: '',
  });

  const [scheduleData, setScheduleData] = useState({
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: [],
  });

  const tabs = [
    { id: 'profile', label: 'Profil', icon: User },
    { id: 'security', label: 'Sécurité', icon: Lock },
    {
      id: 'notifications',
      label: 'Notifications',
      icon: Bell,
      isPremium: true,
    },
    { id: 'school', label: 'École', icon: Building },
    { id: 'schedule', label: 'Horaires', icon: Calendar },
    { id: 'holidays', label: 'Vacances scolaires', icon: CalendarOff },
    { id: 'thresholds', label: "Seuils d'alerte", icon: SlidersHorizontal },
  ];

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const r = await unifiedApi.settings.getProfile();
        if (!r.success) return;
        const data = r.data;
        if (data.user) {
          setProfileData({
            firstName: data.user.firstName || '',
            lastName: data.user.lastName || '',
            email: data.user.email || '',
            phone: data.user.phone || '',
            position: data.user.position || '',
          });
          if (data.user.notificationPreferences) {
            setNotificationSettings((prev) => ({
              ...prev,
              ...data.user.notificationPreferences,
            }));
          }
        }
        if (data.school) {
          setSchoolData({
            schoolName: data.school.schoolName || '',
            address: data.school.address || '',
            phone: data.school.phone || '',
            email: data.school.email || '',
            website: data.school.website || '',
          });
          if (data.school.schedulePlan) {
            setScheduleData(data.school.schedulePlan);
          }
        }
      } catch (err) {
        logger.error('Erreur chargement settings:', err);
        setLoadError(err.message);
      } finally {
        setInitialLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await unifiedApi.settings.updateProfile(profileData);
      if (r.success) {
        toast.success('Profil mis à jour avec succès !');
      } else {
        throw new Error(r.error || r.message || 'Erreur mise à jour');
      }
    } catch (err) {
      toast.error(err.message || 'Erreur lors de la mise à jour du profil');
    } finally {
      setLoading(false);
    }
  };

  const handleSecuritySubmit = async (e) => {
    e.preventDefault();
    if (securityData.newPassword !== securityData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (securityData.newPassword.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    setLoading(true);
    try {
      const r = await unifiedApi.auth.changePassword({
        new_password: securityData.newPassword,
        current_password: securityData.currentPassword,
      });
      if (r.success) {
        toast.success('Mot de passe mis à jour avec succès !');
        setSecurityData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
      } else {
        throw new Error(r.error || r.message || 'Erreur mise à jour mot de passe');
      }
    } catch (err) {
      toast.error(err.message || 'Erreur lors de la mise à jour du mot de passe');
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await unifiedApi.settings.updateNotifications(notificationSettings);
      if (r.success) {
        toast.success('Préférences de notification mises à jour !');
      } else {
        throw new Error(r.error || r.message || 'Erreur mise à jour notifications');
      }
    } catch (err) {
      toast.error(err.message || 'Erreur lors de la mise à jour des notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleSchoolSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await unifiedApi.settings.updateSchool(schoolData);
      if (r.success) {
        toast.success("Informations de l'école mises à jour !");
      } else {
        throw new Error(r.error || r.message || 'Erreur mise à jour école');
      }
    } catch (err) {
      toast.error(err.message || "Erreur lors de la mise à jour des informations de l'école");
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleSubmit = async () => {
    setLoading(true);
    try {
      const r = await unifiedApi.settings.updateSchedule(scheduleData);
      if (r.success) {
        toast.success('Horaires mis à jour avec succès !');
      } else {
        throw new Error(r.error || r.message || 'Erreur mise à jour horaires');
      }
    } catch (err) {
      toast.error(err.message || 'Erreur lors de la sauvegarde des horaires');
    } finally {
      setLoading(false);
    }
  };

  return {
    user,
    isBasicPlan,
    activeTab,
    setActiveTab,
    initialLoading,
    loadError,
    loading,
    showPassword,
    setShowPassword,
    profileData,
    setProfileData,
    securityData,
    setSecurityData,
    notificationSettings,
    setNotificationSettings,
    schoolData,
    setSchoolData,
    scheduleData,
    setScheduleData,
    tabs,
    handleProfileSubmit,
    handleSecuritySubmit,
    handleNotificationSubmit,
    handleSchoolSubmit,
    handleScheduleSubmit,
  };
};

export default useSettings;
